import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor } from '../../App.styles';
import { StepperContext } from '../../contexts';
import { useMetrics } from '../../hooks';
import {
  isAmazonHost,
  metricEvents,
  RingtercomFeature,
  TestId,
} from '../../utils';
import { Button } from '../index';
import {
  Cards,
  Content,
  Header,
  StyledCard,
  TagContainer,
} from './RingtercomCards.styles';

interface RingtercomCard {
  deviceName: React.ReactNode;
  deviceDescription: React.ReactNode;
  deviceIcon?: string;
  tag?: React.ReactNode;
  features: React.ReactNode[];
  shopUrl: string;
  videoSupported: boolean;
}

interface RingtercomCardsProps {
  cards: RingtercomCard[];
}

const RingtercomCards = ({ cards }: RingtercomCardsProps) => {
  const { t } = useTranslation('common');
  const { track } = useMetrics();
  const { stepperData } = useContext(StepperContext);
  const { brand, model, terminals } = stepperData;

  useEffect(() => {
    track(metricEvents.ringtercomCardsLoaded, {
      cardsShown: cards.length,
      cardTypes: cards.map((card) =>
        card.videoSupported ? RingtercomFeature.VIDEO : RingtercomFeature.AUDIO
      ),
      brand: brand,
      model: model?.model_name,
      terminals: terminals,
    });
  }, []);

  return (
    <Cards className="mb-3">
      <div className="row">
        {cards.map(
          (
            {
              tag,
              deviceName,
              deviceDescription,
              features,
              shopUrl,
              deviceIcon,
              videoSupported,
            },
            index
          ) => (
            <div key={index} className={`card-column flex-grow-1 gy-3 col`}>
              <StyledCard data-testid={TestId.RingtercomCard} tabIndex={0}>
                <Header recommended={!index}>
                  {!isAmazonHost() && (
                    <TagContainer recommended={!index}>
                      {deviceIcon && (
                        <img alt="" className="icon" src={deviceIcon}></img>
                      )}
                      {tag && !index && cards.length > 1 && (
                        <div className="tag">{tag}</div>
                      )}
                    </TagContainer>
                  )}
                  <div className="text">
                    <h4>{deviceName}</h4>
                    <p>{deviceDescription}</p>
                  </div>

                  {isAmazonHost() && (
                    <TagContainer recommended={!index}>
                      {tag && cards.length > 1 && (
                        <div className="tag">{tag}</div>
                      )}
                    </TagContainer>
                  )}
                </Header>
                <Content>
                  <div className="text">
                    <p className="fw-bolder">
                      {t('results.success.ringtercomCard.features')}
                    </p>

                    <ul>
                      {features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <Anchor
                    noUnderline
                    href={shopUrl}
                    onClick={() =>
                      track(metricEvents.shopNowBtn, {
                        shopUrl,
                        cardType: videoSupported
                          ? RingtercomFeature.VIDEO
                          : RingtercomFeature.AUDIO,
                        brand: brand,
                        model: model?.model_name,
                        terminals: terminals,
                      })
                    }
                  >
                    <Button
                      secondary={!!index}
                      className={`align-self-center ${
                        isAmazonHost() && 'rounded-pill'
                      }`}
                      type="button"
                    >
                      {t('results.success.ringtercomCard.shopNowBtn')}
                    </Button>
                  </Anchor>
                </Content>
              </StyledCard>
            </div>
          )
        )}
      </div>
    </Cards>
  );
};

export default RingtercomCards;
